import { cn } from '@my/ui';
import { Calendar, ClipboardList, Clock, GripVertical, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import type { Address } from '@my/directory-api';
import { slotLabel, type AssignmentSlotValue, type OrderTask, type OrderTaskResource } from '@my/tms-api';
import { adaptTaskType } from '../../orders/lib/adapters';
import { CountryBadge, DataCell, TaskTypeBadge } from '../../orders/components/badges';
import { taskTypeLabel } from '../../orders/lib/taskTypeConfig';
import type { PlanMovement } from '../lib/movements';
import { SectionBand } from './PlanShared';

/** Allocation cells shown per stop, left to right. */
const SLOTS: { slot: AssignmentSlotValue; label: string }[] = [
  { slot: 'Driver', label: 'Driver' },
  { slot: 'Vehicle', label: 'Vehicle' },
  { slot: 'Trailer', label: 'Trailer' },
];

/** "2026-06-30" → "Tue 30 Jun". Matches the order screen's task strip. */
function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return iso;
  const date = new Date(Date.UTC(y, m - 1, d));
  const weekday = date.toLocaleDateString('en-GB', { weekday: 'short', timeZone: 'UTC' });
  const month = date.toLocaleDateString('en-GB', { month: 'short', timeZone: 'UTC' });
  return `${weekday} ${String(d).padStart(2, '0')} ${month}`;
}

/** The stop's time commitment, mirroring the order screen's pill vocabulary. */
function formatTime(task: OrderTask): string {
  if (task.timeMode === 'AM') return 'AM';
  if (task.timeMode === 'PM') return 'PM';
  // A booking half leaves timeMode null but always carries a precise sail time.
  if (task.plannedTime) return task.plannedTime.slice(0, 5);
  return 'Any';
}

/**
 * Per-stop resource allocation for one movement.
 *
 * <p>
 * Each stop is its own card, laid out like the order screen's task strip — same
 * number badge, type badge, country badge and data cells — so a stop reads as the
 * same kind of object wherever you meet it. The allocation columns take the place
 * of the cargo columns.
 * </p>
 *
 * <p>
 * Reads the flat task-scoped assignments rather than the movement's inline
 * `resources`, because a load's inline copy is deduped across tasks and drops the
 * task id, so it can't say which stop a resource is on.
 * </p>
 */
export function TaskAllocationPanel({
  movement,
  assignments,
  addressById,
  orderNumberById,
  dragging,
  onDropOnTask,
  onReorder,
  onRemoveSlot,
  reordering,
  mutating,
  onClose,
}: {
  movement: PlanMovement;
  /** Every assignment in the org, flat. Filtered per task here. */
  assignments: OrderTaskResource[];
  addressById: Map<string, Address>;
  /** Order id → order number, for labelling stops on a multi-order load. */
  orderNumberById: Map<string, string>;
  /** True while a resource card is being dragged — stops only light up then. */
  dragging: boolean;
  /**
   * Drop a dragged resource onto a stop. Applies from there to the end of the
   * journey — the cascade — which is how a driver or vehicle handover part-way
   * through is expressed. The index and full list come along so the caller can
   * work out what "onwards" means.
   */
  onDropOnTask: (task: OrderTask, index: number, tasks: OrderTask[]) => void;
  /**
   * New stop order, as task ids. Null when reordering isn't available for this
   * movement — see the note on the grip.
   */
  onReorder: ((taskIds: string[]) => void) | null;
  /** Clear one slot on one stop — backs the × on a resource chip. */
  onRemoveSlot: (task: OrderTask, slot: AssignmentSlotValue) => void;
  /** True while a reorder write is in flight, so the grips freeze. */
  reordering: boolean;
  /** True while an assignment write is in flight, so the chips' × freezes. */
  mutating: boolean;
  onClose: () => void;
}) {
  /** Local drag state for reordering — kept apart from the resource-card drag. */
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  /** Stop currently hovered by a resource drag, so the cascade range can preview. */
  const [cascadeFrom, setCascadeFrom] = useState<number | null>(null);
  /** Task id → its assignments, so each card is one lookup. */
  const byTaskId = useMemo(() => {
    const map = new Map<string, OrderTaskResource[]>();
    for (const a of assignments) {
      const list = map.get(a.orderTaskId);
      if (list) list.push(a);
      else map.set(a.orderTaskId, [a]);
    }
    return map;
  }, [assignments]);

  /* Rich header in the band's tab slot — the same breadcrumb shape the Bookings
     screen's expanded panel uses, rather than a bare section title. */
  const header = (
    <div className="flex min-w-0 flex-wrap items-center gap-2 text-[11px]">
      <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded bg-emerald-600 text-white">
        <ClipboardList className="h-3 w-3" />
      </span>
      <span className="font-semibold uppercase tracking-wider text-white">Allocation</span>
      <span className="font-mono text-amber-300">{movement.jobNo}</span>
      <span className="text-slate-500">·</span>
      <span className="truncate text-slate-200">{movement.customer}</span>
      <span className="text-slate-500">·</span>
      <span className="text-slate-300">
        {movement.origin}→{movement.dest}
      </span>
      <span className="text-slate-500">·</span>
      <span className="text-slate-300">
        {movement.tasks.length} stop{movement.tasks.length === 1 ? '' : 's'}
      </span>
    </div>
  );

  return (
    <SectionBand
      label="Allocation"
      tabs={header}
      right={
        <button
          type="button"
          onClick={onClose}
          aria-label="Close allocation"
          title="Close"
          className="rounded-full p-0.5 text-slate-300 transition-colors hover:bg-slate-600 hover:text-white"
        >
          <X className="h-4 w-4" />
        </button>
      }
      className="shrink-0"
    >
      {/* No inner scroll: the panel lives inside the queue's own scrolling table
          now, and a scroll region within a scroll region is miserable to use. */}
      <div className="p-2">
        {movement.tasks.length === 0 ? (
          <p className="px-2 py-6 text-center text-xs italic text-slate-400">
            No stops on this job yet.
          </p>
        ) : (
          <ul className="flex flex-col gap-2">
            {movement.tasks.map((task, index) => (
              <StopCard
                key={task.id}
                task={task}
                index={index}
                address={addressById.get(task.addressId)}
                rows={byTaskId.get(task.id) ?? []}
                // Only shown on a load — on a single order every stop belongs to
                // the same one, so it would be noise on every row.
                orderNumber={
                  movement.kind === 'load'
                    ? (orderNumberById.get(task.orderId) ?? null)
                    : null
                }
                dragging={dragging}
                onDrop={() => onDropOnTask(task, index, movement.tasks)}
                onRemoveSlot={(slot) => onRemoveSlot(task, slot)}
                mutating={mutating}
                canReorder={onReorder !== null && !reordering && movement.tasks.length > 1}
                // A resource drag highlights this stop AND the ones after it, so
                // the reach of the drop is visible before committing to it.
                inCascadeRange={dragging && cascadeFrom !== null && index > cascadeFrom}
                onCascadeHover={() => setCascadeFrom(index)}
                onCascadeLeave={() => setCascadeFrom((cur) => (cur === index ? null : cur))}
                isDragSource={dragIndex === index}
                reorderActive={dragIndex !== null}
                onReorderStart={() => setDragIndex(index)}
                onReorderEnd={() => setDragIndex(null)}
                onReorderDrop={() => {
                  const from = dragIndex;
                  setDragIndex(null);
                  if (from === null || from === index || !onReorder) return;
                  const ids = movement.tasks.map((t) => t.id);
                  const [moved] = ids.splice(from, 1);
                  ids.splice(index, 0, moved!);
                  onReorder(ids);
                }}
              />
            ))}
          </ul>
        )}
      </div>
    </SectionBand>
  );
}

/**
 * One assigned resource, as a removable chip.
 *
 * <p>
 * The × clears just this slot on just this stop. Kept as a chip rather than plain
 * text so it reads as a thing you can take off, and because a subcontracted slot
 * needs a visible tone of its own.
 * </p>
 */
function ResourceChip({
  label,
  isSubcontractor,
  disabled,
  onRemove,
}: {
  label: string;
  isSubcontractor: boolean;
  disabled: boolean;
  onRemove: () => void;
}) {
  return (
    <span
      className={cn(
        'inline-flex max-w-full items-center gap-1 rounded-full border py-0.5 pl-2 pr-1 text-[11px]',
        isSubcontractor
          ? 'border-yellow-300 bg-yellow-50 font-medium text-yellow-800'
          : 'border-slate-200 bg-slate-50 text-slate-700',
      )}
    >
      <span className="truncate" title={label}>
        {label}
      </span>
      <button
        type="button"
        onClick={onRemove}
        disabled={disabled}
        aria-label={`Remove ${label}`}
        title="Remove from this stop"
        className={cn(
          'shrink-0 rounded-full p-0.5 transition-colors',
          isSubcontractor
            ? 'text-yellow-500 hover:bg-yellow-200 hover:text-yellow-900'
            : 'text-slate-400 hover:bg-slate-200 hover:text-slate-700',
          disabled && 'cursor-not-allowed opacity-40 hover:bg-transparent',
        )}
      >
        <X className="h-3 w-3" />
      </button>
    </span>
  );
}

/**
 * One stop, as its own object. Deliberately mirrors `TaskCard`'s collapsed strip
 * on the order screen — number, type, where, when, then the data columns — so the
 * two surfaces don't invent different shapes for the same thing.
 */
function StopCard({
  task,
  index,
  address,
  rows,
  orderNumber,
  dragging,
  onDrop,
  onRemoveSlot,
  mutating,
  canReorder,
  inCascadeRange,
  onCascadeHover,
  onCascadeLeave,
  isDragSource,
  reorderActive,
  onReorderStart,
  onReorderEnd,
  onReorderDrop,
}: {
  task: OrderTask;
  index: number;
  address: Address | undefined;
  rows: OrderTaskResource[];
  /** Owning order's number — set only on a load, where stops can differ. */
  orderNumber: string | null;
  dragging: boolean;
  onDrop: () => void;
  onRemoveSlot: (slot: AssignmentSlotValue) => void;
  mutating: boolean;
  canReorder: boolean;
  /** True when a cascade from the hovered stop would reach this one. */
  inCascadeRange: boolean;
  onCascadeHover: () => void;
  onCascadeLeave: () => void;
  isDragSource: boolean;
  /** True while ANY stop is being dragged, so the others accept a reorder drop. */
  reorderActive: boolean;
  onReorderStart: () => void;
  onReorderEnd: () => void;
  onReorderDrop: () => void;
}) {
  const [dragOver, setDragOver] = useState(false);
  /**
   * Reordering is armed from the grip only. The card is already a drop target for
   * resource cards, so making the whole thing draggable would make "am I assigning
   * or re-sequencing?" ambiguous — and a `draggable` ancestor also swallows text
   * selection inside the card.
   */
  const [gripHeld, setGripHeld] = useState(false);
  const subtitleParts = [address?.city, address?.countryIsoCode].filter(
    (s): s is string => !!s && s.trim().length > 0,
  );
  // Cargo-item-scoped rows are part of the stop, not the whole of it — surfaced as
  // a note rather than rendered in the slot columns, which would misrepresent them.
  const splitCount = rows.filter((r) => r.orderTaskCargoItemId != null).length;

  return (
    <li
      draggable={canReorder && gripHeld}
      onDragStart={(e) => {
        // Firefox ignores a drag with no payload.
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', task.id);
        onReorderStart();
      }}
      onDragEnd={() => {
        setGripHeld(false);
        onReorderEnd();
      }}
      onDragOver={(e) => {
        // Two kinds of drag can land here: a resource card (assign to this stop)
        // and another stop card (re-sequence). Neither is accepted unless one is
        // actually in flight.
        if (!dragging && !reorderActive) return;
        if (reorderActive && isDragSource) return;
        // Without preventDefault the browser refuses the drop outright.
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        setDragOver(true);
        if (!reorderActive) onCascadeHover();
      }}
      onDragLeave={() => {
        setDragOver(false);
        onCascadeLeave();
      }}
      onDrop={(e) => {
        if (!dragging && !reorderActive) return;
        e.preventDefault();
        setDragOver(false);
        onCascadeLeave();
        // Reorder wins when a stop is the thing being dragged — a resource drag
        // and a stop drag can't be in flight at once.
        if (reorderActive) onReorderDrop();
        else onDrop();
      }}
      className={cn(
        'flex flex-wrap items-center gap-x-6 gap-y-2 rounded-md border bg-white px-3 py-2',
        // Emerald matches the Assign button and the queue row's drop state — the
        // same action, just aimed at one stop instead of the whole job.
        dragOver
          ? 'border-emerald-400 bg-emerald-50 ring-1 ring-inset ring-emerald-400'
          : // Lighter than the drop target: these stops are included by the
            // cascade, not the one being aimed at.
            inCascadeRange
            ? 'border-emerald-200 bg-emerald-50/40'
            : 'border-slate-200 hover:border-slate-300',
        isDragSource && 'opacity-40',
      )}
    >
      {/* Position badge doubles as the re-sequence grip, as on the order screen's
          task strip — no extra column, and a familiar affordance. */}
      <span
        onMouseDown={() => canReorder && setGripHeld(true)}
        onMouseUp={() => setGripHeld(false)}
        title={canReorder ? 'Drag to re-sequence this stop' : undefined}
        className={cn(
          'group/grip flex h-6 w-6 shrink-0 items-center justify-center rounded-sm bg-slate-100 text-[11px] font-semibold text-slate-700',
          canReorder && 'cursor-grab hover:bg-slate-200 active:cursor-grabbing',
        )}
      >
        {canReorder ? (
          <>
            <span className="group-hover/grip:hidden">{index + 1}</span>
            <GripVertical className="hidden h-3.5 w-3.5 text-slate-500 group-hover/grip:block" />
          </>
        ) : (
          index + 1
        )}
      </span>

      <TaskTypeBadge type={adaptTaskType(task.type)} label={taskTypeLabel(task.type)} />

      <p className="min-w-0 flex-1 truncate text-sm">
        <span className="font-semibold text-slate-900">{address?.name ?? 'Unknown site'}</span>
        {subtitleParts.length > 0 && (
          <span className="font-normal text-slate-500">, {subtitleParts.join(', ')}</span>
        )}
        {orderNumber && (
          <span className="ml-2 rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[10px] font-medium text-slate-600">
            {orderNumber}
          </span>
        )}
        {splitCount > 0 && (
          <span className="ml-2 text-[10px] italic text-slate-400">
            {splitCount} cargo item{splitCount === 1 ? '' : 's'} allocated separately
          </span>
        )}
      </p>

      {address?.countryIsoCode && <CountryBadge code={address.countryIsoCode} />}

      <span className="flex flex-col gap-0.5 whitespace-nowrap text-xs text-slate-500">
        <span className="inline-flex items-center gap-1.5">
          <Calendar className="h-3 w-3 shrink-0 text-slate-400" />
          {formatDate(task.plannedDate)}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <Clock className="h-3 w-3 shrink-0 text-slate-400" />
          {formatTime(task)}
        </span>
      </span>

      {SLOTS.map(({ slot, label }) => {
        const row = rows.find(
          (r) => r.slot === slot && r.slotIndex === 1 && r.orderTaskCargoItemId == null,
        );
        const value = slotLabel(row);
        return (
          <div key={slot} className="w-32 shrink-0">
            <DataCell
              label={label}
              value={
                value ? (
                  <ResourceChip
                    label={value}
                    isSubcontractor={row?.isSubcontractor ?? false}
                    disabled={mutating}
                    onRemove={() => onRemoveSlot(slot)}
                  />
                ) : (
                  <span className="text-slate-300">—</span>
                )
              }
            />
          </div>
        );
      })}
    </li>
  );
}
